<template>
  <div class="course-detail">
    <!-- 添加童趣装饰元素 
    <div class="course-fun-elements">
      <div class="book book1">📚</div>
      <div class="book book2">📖</div>
      <div class="pencil pencil1">✏️</div>
      <div class="pencil pencil2">✏️</div>
      <div class="star star1">⭐</div>
      <div class="star star2">⭐</div>
      <div class="lightbulb lightbulb1">💡</div>
      <div class="lightbulb lightbulb2">💡</div>
      <div class="trophy trophy1">🏆</div>
      <div class="trophy trophy2">🏆</div>
    </div>
-->
    <!-- 顶部导航栏 -->
    <div class="nav-bar">
      <div class="nav-content">
        <div class="course-info">
          <h1 class="course-title">{{ displayCourseName }}</h1>
          <el-button
            type="primary"
            class="code-btn"
            :disabled="!canGotoCode"
            @click="gotoCode"
          >
            <i class="el-icon-edit"></i> 去实操
          </el-button>
        </div>
      </div>
    </div>

    <div
      class="main-wrap"
      v-loading="!listLoadDone"
      element-loading-text="加载视频列表..."
      element-loading-spinner="el-icon-loading"
    >
    <!-- 主要内容区域 -->
    <div v-if="listLoadDone && !hasVideos" class="main-content main-content--empty">
      <el-empty :description="emptyDescription">
        <el-button type="primary" size="small" @click="goCourseHome">返回视频列表</el-button>
        <el-button size="small" @click="reloadVideos">重新加载</el-button>
      </el-empty>
    </div>

    <div v-else-if="listLoadDone && hasVideos" class="main-content">
      <!-- 左侧视频播放区域 -->
      <div class="video-section">
        <div
          class="video-container"
          v-loading="loading"
          element-loading-background="rgba(0,0,0,0.65)"
        >
          <template v-if="currentVideo && !currentVideoInvalid">
            <video-player
              :key="playerKey"
              class="video-player"
              ref="videoPlayer"
              :playsinline="true"
              :options="playerOptionsObj"
              @ended="onPlayerEnded($event)"
              @pause="onPlayerPause($event)"
              @timeupdate="onPlayerTimeupdate($event)"
              @error="onPlayerError($event)"
              @loadeddata="onPlayerLoadeddata($event)"
            ></video-player>
          </template>
          <div v-else class="video-placeholder">
            <el-empty :description="placeholderDescription" :image-size="100" />
            <div v-if="currentVideoInvalid" class="placeholder-actions">
              <el-button type="primary" size="small" @click="retryCurrentVideo">重新加载</el-button>
              <el-button v-if="nextPlayableVideo" size="small" @click="playVideoNew(nextPlayableVideo)">播放下一节</el-button>
            </div>
          </div>
          <div
            v-if="videoError && currentVideo && !currentVideoInvalid"
            class="player-error-wrap"
          >
            <el-alert
              class="player-error-alert"
              type="error"
              :closable="false"
              show-icon
              title="当前视频无法播放"
              :description="playErrorHint"
            />
            <div class="error-alert-actions">
              <el-button type="primary" size="mini" @click="retryCurrentVideo">重试</el-button>
              <el-button v-if="nextPlayableVideo" size="mini" @click="playVideoNew(nextPlayableVideo)">播放下一节</el-button>
              <el-button size="mini" @click="goCourseHome">返回列表</el-button>
            </div>
          </div>
        </div>
        <div class="video-info">
          <h2 class="video-title">
            {{ currentVideo ? currentVideo.videoName : "请选择视频" }}
          </h2>
          <!-- 播放量、上传时间等字段未稳定返回时先隐藏，由播放器控件展示进度与时长 -->
          <!--
          <div class="video-meta">
            <span class="play-count">
              <i class="el-icon-video-play"></i>
              {{ currentVideo ? currentVideo.playCount || "0" : "0" }} 次播放
            </span>
            <span class="upload-time">
              <i class="el-icon-time"></i>
              {{
                currentVideo
                  ? currentVideo.uploadTime || "未知时间"
                  : "未知时间"
              }}
            </span>
          </div>
          -->
        </div>
      </div>

      <!-- 右侧视频列表区域 -->
      <div class="video-list-section">
        <div class="section-header">
          <h2>视频列表</h2>
          <p v-if="courses.length === 1" class="section-hint">本课仅含 1 节内容</p>
          <p v-else class="section-hint">共 {{ courses.length }} 节</p>
        </div>
        <div class="video-list">
          <div
            v-for="(course, vidx) in courses"
            :key="course.id || course.videoId || 'v-' + vidx"
            class="video-item"
            :class="{
              active: currentVideo && currentVideo.id === course.id,
              'is-invalid': !isValidVideoUrl(course.address),
            }"
            @click="onSelectListItem(course)"
          >
            <div class="video-thumbnail">
              <img
                :src="course.cover"
                :alt="course.videoName"
                @error="onCoverError"
              />
            </div>
            <div class="video-info">
              <h3 class="video-title">{{ course.videoName }}</h3>
              <!--
              <div class="video-meta">
                <span class="episode">
                  <i class="el-icon-video-camera"></i>
                  第{{ course.episode || "0" }}集
                </span>
              </div>
              -->
            </div>
          </div>
        </div>
      </div>
    </div>
    </div>
  </div>
</template>

<script>
// import VueVideo from '../videoComp/VideoComp.vue';

/** 课程详情页 video.js 公共配置：控件、倍速、全屏、进度、音量、预加载 */
function buildCourseDetailPlayerOptions(poster, overrides) {
  return Object.assign(
    {
      controls: true,
      playbackRates: [0.5, 0.75, 1.0, 1.25, 1.5, 2.0],
      preload: "auto",
      language: "zh-CN",
      aspectRatio: "16:9",
      fluid: true,
      poster: poster || "",
      notSupportedMessage: "此视频暂无法播放...",
      controlBar: {
        playToggle: true,
        volumePanel: { inline: true },
        currentTimeDisplay: true,
        timeDivider: true,
        durationDisplay: true,
        remainingTimeDisplay: false,
        progressControl: true,
        playbackRateMenuButton: true,
        fullscreenToggle: true,
      },
    },
    overrides
  );
}

export default {
  data() {
    return {
      courses: [],
      course: {},
      videoDialogVisible: false,
      currentVideoSrc: "",
      videoList: {},
      autoplay: true,
      loading: false,
      listLoadDone: false,
      videosLoadError: false,
      playPoint: 0,
      currentVideo: null,
      playVideoId: 0,
      courseId: "",
      userId: "",
      assignmentId: "",
      mp4Pic: "",
      fileType: "mp4",
      playerOptionsObj: buildCourseDetailPlayerOptions("", {
        autoplay: false,
        muted: false,
        loop: false,
        sources: [{ type: "video/mp4", src: "" }],
      }),
      showVideo: false,
      playerKey: 0,
      videoError: false,
      playErrorHint:
        "可能是网络不稳定、资源已失效或格式不受支持。您可尝试重试或换一节观看。",
    };
  },
  computed: {
    hasVideos() {
      return Array.isArray(this.courses) && this.courses.length > 0;
    },
    displayCourseName() {
      return (this.course && this.course.name) || "课程详情";
    },
    canGotoCode() {
      return !!this.courseId;
    },
    currentVideoInvalid() {
      return (
        this.currentVideo &&
        !this.isValidVideoUrl(this.currentVideo.address)
      );
    },
    placeholderDescription() {
      if (!this.currentVideo) {
        return "请在右侧列表选择一节视频";
      }
      if (this.currentVideoInvalid) {
        return "本节未配置有效的视频地址，暂时无法播放";
      }
      return "暂无可播放内容";
    },
    emptyDescription() {
      if (this.videosLoadError) {
        return "视频列表加载失败，请检查网络或稍后重试";
      }
      return "本课程暂无视频内容";
    },
    nextPlayableVideo() {
      if (!this.currentVideo || !this.hasVideos) return null;
      const idx = this.courses.findIndex((v) => this.isSameVideoItem(v, this.currentVideo));
      const start = idx >= 0 ? idx + 1 : 0;
      for (let i = start; i < this.courses.length; i++) {
        if (this.isValidVideoUrl(this.courses[i].address)) {
          return this.courses[i];
        }
      }
      return null;
    },
  },
  // components:{
  //   VueVideo
  //       },
  mounted() {
    //开始就准备跳转到播放记录点  TODO
    // this.$refs.videoPlayer.$refs.video.addEventListener("loadeddata", (e) => {
    //   console.log("提示视频的元数据已加载");
    //   console.log(e);
    //   this.loading = false;
    // });
    
    // 课程详情页进入动画
    this.enterAnimation();
  },
  created() {
    this.setBasicParam();
    this.userId = localStorage.getItem("ms_userId");
    //之前是根据用户id查询视频列表，现更改为根据课程id查询
    this.getVideosByCourseId();
    this.getCourse();
    //this.getCourseVideoList();
  },

  beforeDestroy() {
    if (this.currentVideo && this.courseId && !this.currentVideoInvalid) {
      this.recordVideoPlay();
    }
  },

  methods: {
    isValidVideoUrl(url) {
      if (url === undefined || url === null) return false;
      const s = String(url).trim();
      if (!s || s === "undefined" || s === "null") return false;
      return /^https?:\/\//i.test(s) || s.startsWith("//");
    },
    isSameVideoItem(a, b) {
      if (!a || !b) return false;
      if (a.id != null && b.id != null && a.id === b.id) return true;
      if (a.videoId != null && b.videoId != null && a.videoId === b.videoId) {
        return true;
      }
      return false;
    },
    findFirstPlayableVideo(list) {
      if (!Array.isArray(list)) return null;
      return list.find((v) => this.isValidVideoUrl(v && v.address)) || null;
    },
    goCourseHome() {
      this.$router.push({ name: "coursehome" });
    },
    reloadVideos() {
      this.listLoadDone = false;
      this.videosLoadError = false;
      this.getVideosByCourseId();
    },
    onCoverError(e) {
      e.target.src = require('@/assets/plant/cat3.png');
    },
    async onSelectListItem(video) {
      if (!this.isValidVideoUrl(video.address)) {
        this.$message.warning("该节暂无有效播放地址");
        return;
      }
      await this.playVideoNew(video);
    },
    enterAnimation() {
      // 为课程详情页元素添加进入动画
      const elements = document.querySelectorAll(".nav-bar, .main-wrap");
      elements.forEach((el, index) => {
        el.style.opacity = '0';
        el.style.transform = 'translateY(50px) scale(0.9)';
        el.style.transition = 'all 0.2s cubic-bezier(0.68, -0.55, 0.27, 1.55)';
        
        setTimeout(() => {
          el.style.opacity = '1';
          el.style.transform = 'translateY(0) scale(1)';
        }, index * 40);
      });
    },
    loadstart(e) {
      console.log("提示视频的元数据已加载", e);
    },
    onPlayerPause(player) {
      console.log("player pause!", player);
    },
    onPlayerEnded() {
      const next = this.nextPlayableVideo;
      if (next) {
        this.playVideoNew(next);
      }
    },
    onPlayerTimeupdate(player) {
      if (!player || typeof player.currentTime !== "function") return;
      try {
        this.playPoint = Math.floor(player.currentTime());
      } catch (e) {
        /* ignore */
      }
    },
    playerReadied(player) {
      player.currentTime = this.playPoint;
      console.log("example 01: the player is readied", player);
    },
    getCourse() {
      let params = {
        courseId: this.courseId,
      };
      this.$request
        .callApi("GET", this.$robotApi.getCourseById, params)
        .then((res) => {
          this.course = res.data || {};
        })
        .catch(() => {
          this.course = {};
        });
    },
    getCourseVideoList() {
      let params = {
        studentId: 1,
      };
      this.$request
        .callApi("GET", this.$robotApi.courseVideoList, params)
        .then((res) => {
          this.courses = res.data;
          this.videoList.url = this.courses[0].address;
          this.videoList.title = "标题1";
          this.playVideoId = this.courses[0].videoId;
        });
      //TODO  根据playVideoId查询播放记录
      let params2 = {
        videoId: this.playVideoId,
        userId: this.userId,
      };
      this.$request
        .callApi("GET", this.$robotApi.getVideoPlayHis, params2)
        .then((res) => {
          this.playPoint = res.data.playPoint;
        });
    },
    async getVideosByCourseId() {
      this.videosLoadError = false;
      try {
        const params = { courseId: this.courseId };
        const res = await this.$request.callApi(
          "GET",
          this.$robotApi.getVideosByCourseId,
          params
        );

        const list = Array.isArray(res.data) ? res.data : [];
        this.courses = list;

        if (list.length === 0) {
          this.currentVideo = null;
          this.videoError = false;
          return;
        }

        const firstPlayable = this.findFirstPlayableVideo(list);
        if (firstPlayable) {
          await this.playVideoNew(firstPlayable);
        } else {
          this.currentVideo = list[0];
          this.videoError = false;
          this.playPoint = 0;
          this.$message.warning("本课暂无有效的视频播放地址");
        }
      } catch (error) {
        console.error("获取视频列表失败:", error);
        this.videosLoadError = true;
        this.courses = [];
        this.currentVideo = null;
        this.$message.error("获取视频列表失败，请稍后重试");
      } finally {
        this.listLoadDone = true;
      }
    },
    async playVideoNew(video) {
      if (!video) return;
      try {
        this.loading = true;
        this.videoError = false;
        this.playErrorHint =
          "可能是网络不稳定、资源已失效或格式不受支持。您可尝试重试或换一节观看。";

        this.currentVideo = video;
        this.playPoint = 0;

        if (!this.isValidVideoUrl(video.address)) {
          this.loading = false;
          return;
        }

        if (video.videoId) {
          await this.getVideoPlayHistory(video.videoId);
        }

        this.playerKey += 1;

        this.playerOptionsObj = buildCourseDetailPlayerOptions(this.mp4Pic, {
          autoplay: true,
          muted: false,
          loop: false,
          sources: [{ type: "video/mp4", src: video.address.trim() }],
        });

        await this.$nextTick();

        if (this.$refs.videoPlayer && this.$refs.videoPlayer.player) {
          this.$refs.videoPlayer.player.load();
          if (this.playPoint > 0) {
            this.$refs.videoPlayer.player.currentTime(this.playPoint);
          }
        }
      } catch (error) {
        console.error("播放视频失败:", error);
        this.videoError = true;
        this.playErrorHint = "初始化播放器失败，请重试或更换其他节次。";
        this.$message.error("视频加载失败，请稍后重试");
      } finally {
        this.loading = false;
      }
    },
    async retryCurrentVideo() {
      if (!this.currentVideo) return;
      if (this.currentVideoInvalid) {
        this.reloadVideos();
        return;
      }
      this.videoError = false;
      this.playErrorHint =
        "可能是网络不稳定、资源已失效或格式不受支持。您可尝试重试或换一节观看。";
      this.playerKey += 1;
      await this.$nextTick();
      await this.playVideoNew(this.currentVideo);
    },
    initPlayerOptions(videoUrl) {
      return buildCourseDetailPlayerOptions(this.mp4Pic, {
        autoplay: false,
        muted: false,
        loop: false,
        sources: [{ type: "video/mp4", src: videoUrl }],
      });
    },
    async getVideoPlayHistory(videoId) {
      try {
        const params = {
          videoId: videoId,
          userId: this.userId,
        };
        const res = await this.$request.callApi(
          "GET",
          this.$robotApi.getVideoPlayHis,
          params
        );
        if (res.data) {
          this.playPoint = res.data.playPoint || 0;
        }
      } catch (error) {
        console.error("获取播放记录失败:", error);
      }
    },
    onPlayerError() {
      this.videoError = true;
      this.loading = false;
      this.playErrorHint =
        "播放过程中出现异常，请检查网络或更换节次。若多次失败请联系管理员。";
    },
    // 视频加载完成事件
    onPlayerLoadeddata(player) {
      this.videoError = false;
      this.loading = false;
      if (this.playPoint > 0) {
        player.currentTime(this.playPoint);
      }
    },
    //后台记录当前播放进度以后续播  TODO
    recordVideoPlay() {
      if (!this.currentVideo || !this.courseId || this.currentVideoInvalid) {
        return;
      }
      let params = {
        playPoint: this.playPoint,
        userId: this.userId,
        courseId: this.courseId,
        courseName: (this.course && this.course.name) || "",
      };
      Object.assign(this.currentVideo, params);
      this.$request
        .callApi("POST", this.$robotApi.addCourseRecord, params)
        .then((res) => {
          console.log(res.data);
        });
    },

    playVideo(videos) {
      // 打开一个新窗口来播放视频
      const videoWindow = window.open("", "_blank", "width=600,height=400");

      // 在新窗口中加载视频播放页面
      videoWindow.document.write(`
    <html>
      <head>
        <title>视频播放</title>
      </head>
      <body>
        <video width="100%" controls>
          <source src="${videos.address}" type="video/mp4">
          您的浏览器不支持视频标签。
        </video>
      </body>
    </html>

  `);
    },
    gotoCode() {
      if (this.assignmentId) {
        this.storInfo(3, "", "", this.assignmentId);
      } else {
        this.storInfo(1, this.courseId, "", "");
      }

      this.$router.push({
        path: "/Game/scratch",
        query: {
          courseId: this.courseId,
          assignmentId: this.assignmentId,
        },
      });
    },
    storInfo(type, courseId, codingRecordId, assignmentId) {
      window.localStorage.setItem("userId", this.userId);
      window.localStorage.setItem("userName", this.userName);
      window.localStorage.setItem("avatar", "");
      //1 课程学习跳转  2编程记录跳转  3种植任务跳转
      window.localStorage.setItem("codeType", type);
      if (type === 1) {
        window.localStorage.setItem("courseId", courseId);
        window.localStorage.removeItem("codingRecordId");
        window.localStorage.removeItem("assignmentId");
      } else if (type === 2) {
        window.localStorage.removeItem("courseId");
        window.localStorage.setItem("codingRecordId", codingRecordId);
        window.localStorage.removeItem("assignmentId");
      } else if (type === 3) {
        window.localStorage.removeItem("courseId");
        window.localStorage.removeItem("codingRecordId");
        window.localStorage.setItem("assignmentId", assignmentId);
      }
    },
    setBasicParam() {
      // 先从 params 获取
      this.courseId = this.$route.params.courseId;
      this.assignmentId = this.$route.params.assignmentId;
      // 如果 params 没有，再从 query 获取
      if (!this.courseId) {
        this.courseId = this.$route.query.courseId;
      }
      if (!this.assignmentId) {
        this.assignmentId = this.$route.query.assignmentId;
      }
      // 下面的逻辑不变
      if (this.courseId) {
        localStorage.setItem("videoCourseId", this.courseId);
      } else {
        this.courseId = localStorage.getItem("videoCourseId");
      }
      if (this.assignmentId) {
        localStorage.setItem("videoAssignmentId", this.assignmentId);
      } else {
        this.assignmentId = localStorage.getItem("videoAssignmentId");
      }
    },
    // playerOptions() {
    //   const playerOptionsObj = {
    //     playbackRates: [0.5, 1.0, 1.5, 2.0], // 视频播放速度
    //     autoplay: false, // 如果true，浏览器准备好时开始回放---是否一加载即播放。
    //     muted: false, // 默认情况下将会消除任何音频。
    //     loop: false, // 导致视频一结束就重新开始。
    //     // preload: 'auto', // 建议浏览器在<video>加载元素后是否应该开始下载视频数据。auto浏览器选择最佳行为,立即开始加载视频（如果浏览器支持）。
    //     language: "zh-CN",
    //     aspectRatio: "16:9", // 将播放器置于流畅模式，并在计算播放器的动态大小时使用该值。值应该代表一个比例 - 用冒号分隔的两个数字（例如"16:9"或"4:3"）。
    //     fluid: true, // 当true时，Video.js player将拥有流体大小。换句话说，它将按比例缩放以适应其容器。
    //     sources: [
    //       {
    //         type: "video/" + this.fileType, // 资源格式写法：'video/mp4'，否则控制台会出现notSupportedMessage设置的错误。
    //         src: "", // 视频url地址
    //       },
    //     ],
    //     poster: this.mp4Pic, // 视频封面地址
    //     // width: document.documentElement.clientWidth,
    //     // height: this.fileAreaHeight,	// 设置高度，fluid需要设置成flase

    //     notSupportedMessage: "此视频暂无法播放...", // 允许覆盖Video.js无法播放媒体源时显示的默认信息。
    //     controlBar: {
    //       // timeDivider: true,
    //       durationDisplay: true,
    //       remainingTimeDisplay: false,
    //       fullscreenToggle: true, //全屏按钮
    //     },
    //   };
    //   return playerOptionsObj;
    // },
  },
};
</script>

<style lang="scss" scoped>
.course-detail {
  min-height: 100vh;
  background: #f4f4f4;
  position: relative;
  overflow: hidden;
}

/* 童趣装饰元素 */
.course-fun-elements {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.book {
  position: absolute;
  font-size: 35px;
  animation: floatBook 2s ease-in-out infinite;
}

.book.book1 {
  top: 10%;
  left: 8%;
  animation-delay: 0s;
}

.book.book2 {
  top: 20%;
  right: 12%;
  animation-delay: 1s;
}

.pencil {
  position: absolute;
  font-size: 25px;
  animation: rotatePencil 2s ease-in-out infinite;
}

.pencil.pencil1 {
  top: 35%;
  left: 15%;
  animation-delay: 1s;
}

.pencil.pencil2 {
  top: 45%;
  right: 18%;
  animation-delay: 2s;
}

.star {
  position: absolute;
  font-size: 30px;
  animation: twinkleStar 2s ease-in-out infinite;
}

.star.star1 {
  top: 15%;
  left: 50%;
  animation-delay: 0.3s;
}

.star.star2 {
  top: 25%;
  right: 25%;
  animation-delay: 1s;
}

.lightbulb {
  position: absolute;
  font-size: 28px;
  animation: glowLightbulb 2s ease-in-out infinite;
}

.lightbulb.lightbulb1 {
  bottom: 30%;
  left: 10%;
  animation-delay: 0.2s;
}

.lightbulb.lightbulb2 {
  bottom: 40%;
  right: 15%;
  animation-delay: 1s;
}

.trophy {
  position: absolute;
  font-size: 32px;
  animation: bounceTrophy 2s ease-in-out infinite;
}

.trophy.trophy1 {
  bottom: 20%;
  left: 25%;
  animation-delay: 0.5s;
}

.trophy.trophy2 {
  bottom: 25%;
  right: 8%;
  animation-delay: 1.5s;
}

@keyframes floatBook {
  0%, 100% {
    transform: translateY(0) rotate(0deg);
  }
  50% {
    transform: translateY(-30px) rotate(5deg);
  }
}

@keyframes rotatePencil {
  0%, 100% {
    transform: rotate(0deg) translateY(0);
  }
  50% {
    transform: rotate(180deg) translateY(-15px);
  }
}

@keyframes twinkleStar {
  0%, 100% {
    opacity: 0.3;
    transform: scale(1) rotate(0deg);
  }
  50% {
    opacity: 1;
    transform: scale(1.3) rotate(180deg);
  }
}

@keyframes glowLightbulb {
  0%, 100% {
    opacity: 0.7;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.2);
  }
}

@keyframes bounceTrophy {
  0%, 100% {
    transform: translateY(0) scale(1);
  }
  50% {
    transform: translateY(-20px) scale(1.1);
  }
}

.nav-bar {
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  position: sticky;
  top: 0;
  z-index: 100;
}

.nav-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 16px 20px;
}

.course-info {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .course-title {
    margin: 0;
    font-size: 20px;
    color: #333;
    font-weight: 600;
  }

  .code-btn {
    padding: 8px 20px;
    font-size: 14px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    gap: 6px;
  }
}

.main-content {
  max-width: 1400px;
  margin: 20px auto;
  padding: 0 20px;
  display: grid;
  grid-template-columns: 1fr 300px;
  gap: 20px;
}

.video-section {
  .video-container {
    background: #fff;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    margin-bottom: 16px;
  }

  .video-player {
    width: 100%;
    aspect-ratio: 16/9;
  }

  .video-info {
    background: #fff;
    padding: 16px;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

    .video-title {
      margin: 0 0 12px;
      font-size: 18px;
      color: #333;
      font-weight: 600;
    }

    .video-meta {
      display: flex;
      align-items: center;
      gap: 16px;
      font-size: 14px;
      color: #999;

      i {
        margin-right: 4px;
      }
    }
  }
}

.video-list-section {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  overflow: hidden;

  .section-header {
    padding: 16px;
    border-bottom: 1px solid #eee;

    h2 {
      margin: 0;
      font-size: 16px;
      color: #333;
      font-weight: 600;
    }

    .section-hint {
      margin: 6px 0 0;
      font-size: 12px;
      color: #909399;
      font-weight: normal;
    }
  }

  .video-list {
    max-height: calc(100vh - 300px);
    overflow-y: auto;
  }

  .video-item {
    display: flex;
    padding: 12px;
    cursor: pointer;
    transition: all 0.3s ease;
    border-bottom: 1px solid #eee;

    &:hover {
      background: #f8f8f8;
    }

    &.active {
      background: #e6f7ff;
    }

    &.is-invalid {
      opacity: 0.75;
      cursor: not-allowed;
    }

    .video-thumbnail {
      position: relative;
      width: 160px;
      height: 90px;
      margin-right: 12px;
      border-radius: 4px;
      overflow: hidden;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }

      .duration {
        position: absolute;
        bottom: 4px;
        right: 4px;
        background: rgba(0, 0, 0, 0.7);
        color: #fff;
        padding: 2px 4px;
        border-radius: 2px;
        font-size: 12px;
      }
    }

    .video-info {
      flex: 1;
      display: flex;
      flex-direction: column;
      justify-content: space-between;

      .video-title {
        margin: 0;
        font-size: 14px;
        color: #333;
        line-height: 1.4;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      .video-meta {
        font-size: 12px;
        color: #999;

        i {
          margin-right: 4px;
        }
      }
    }
  }
}

.main-wrap {
  min-height: 320px;
}

.main-content--empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.video-placeholder {
  min-height: 260px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: #fafafa;
}

.placeholder-actions {
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
}

.player-error-wrap {
  padding: 12px 16px 16px;
  border-top: 1px solid #fde2e2;
  background: #fff;
}

.player-error-alert {
  margin-bottom: 10px;
}

.error-alert-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

// 响应式调整
@media (max-width: 1200px) {
  .main-content {
    grid-template-columns: 1fr;
  }

  .video-list-section {
    max-width: 800px;
    margin: 0 auto;
  }
}

@media (max-width: 768px) {
  .video-item {
    .video-thumbnail {
      width: 120px;
      height: 68px;
    }
  }
}
</style>